import '../init';
import { serviceContractTypeSelectDirective } from '@app/modules/service-contract-types/select/service-contract-type-select-directive';
import {
    alertDirective,
    compareToDirective,
    formatterDurationDirective,
    inputEmailDirective,
    inputPasswordConfirmationDirective,
    inputPasswordDirective,
    inputSelectDirective,
    inputTextDirective,
    inputTimeDirective,
    loadingDirective,
    panelDirective,
} from '@shared/directives';
import { inputDurationDirective, inputTextareaDirective } from '@shared/directives/inputs';
import { contractGuaranteesDirective } from '@shared/directives/inputs/contract-guarantees/contract-guarantees-directive';
import { inputSwitchDirective } from '@shared/directives/inputs/input-switch-directive';
import { errorHandlerProvider } from '@shared/services/error-handler-provider';
import { toastProvider } from '@shared/services/toast-provider';
import uiRouter from '@uirouter/angularjs';
import ngMaterial from 'angular-material';
import ngMessages from 'angular-messages';
import angularMoment from 'angular-moment';

import { sortByTimeFilter } from '../app/filters';
import { sortHourlyRateRangesFilter } from '../app/filters/sort-hourly-rate-ranges';
import { bookingTypeFetcherProvider } from '../app/modules/booking-types/booking-type-fetcher';
import { carerContractTypeFetcherProvider } from '../app/modules/carer-contract-types/carer-contract-type-fetcher';
import { serviceContractTypeFetcherProvider } from '../app/modules/service-contract-types/service-contract-type-fetcher';
import { settingsFetcherProvider } from '../app/modules/settings/settings-fetcher';
import { specialDayFetcherProvider } from '../app/modules/special-days/special-day-fetcher';
import { teamFetcherProvider } from '../app/modules/teams/team-fetcher';
import { userFetcherProvider } from '../app/modules/users';

import {
    appProvider,
    configProvider,
    eventEmitterProvider,
    paginatorProvider,
    storageProvider,
    utilProvider,
} from '../app/services';
import { formatterCurrencyDirective } from '../shared/directives/formatter-currency-directive';
import { inputCheckboxDirective } from '../shared/directives/inputs/input-checkbox-directive';
import { inputMultiEmailDirective } from '../shared/directives/inputs/input-multi-email-directive';
import { inputCurrencyDirective } from '../shared/directives/inputs/input-currency-directive';
import * as theme from '../shared/theme';
import * as bookingTypes from './booking-types/booking-types';
import * as carerContracts from './carer-contracts/carer-contracts';
import * as clientServiceContracts from './client-service-contracts/client-service-contracts';
import * as company from './company/company';
import { breadcrumbsDirective } from './directives/breadcrumbs-directive';
import * as finish from './finish/finish';
import './install.scss';

import { installationServiceProvider } from './services/installation-service';
import * as teams from './teams/teams';
import * as users from './users/users';
import * as welcome from './welcome/welcome';

const app = angular.module('Installation', [
    angularMoment,
    ngMaterial,
    ngMessages,
    uiRouter,
    'ngLocale',
]);

app.config(appConfig);
app.run(appRun);

app.directive('compareTo', compareToDirective);

app.directive('appAlert', alertDirective);
app.directive('appContractGuarantees', contractGuaranteesDirective);
app.directive('appInputCheckbox', inputCheckboxDirective);
app.directive('appInputCurrency', inputCurrencyDirective);
app.directive('appInputDuration', inputDurationDirective);
app.directive('appInputEmail', inputEmailDirective);
app.directive('appInputMultiEmail', inputMultiEmailDirective);
app.directive('appInputPassword', inputPasswordDirective);
app.directive('appInputPasswordConfirmation', inputPasswordConfirmationDirective);
app.directive('appInputSelect', inputSelectDirective);
app.directive('appInputSwitch', inputSwitchDirective);
app.directive('appInputText', inputTextDirective);
app.directive('appInputTextarea', inputTextareaDirective);
app.directive('appInputTime', inputTimeDirective);
app.directive('appLoading', loadingDirective);
app.directive('appPanel', panelDirective);
app.directive('formatterCurrency', formatterCurrencyDirective);
app.directive('formatterDuration', formatterDurationDirective);

app.directive('installationBreadcrumbs', breadcrumbsDirective);

app.directive('serviceContractTypeSelect', serviceContractTypeSelectDirective);

app.filter('sortByTime', sortByTimeFilter);
app.filter('sortHourlyRateRanges', sortHourlyRateRangesFilter);

app.provider('$app', appProvider as ng.IServiceProviderFactory);
app.provider('$toast', toastProvider as ng.IServiceProviderFactory);
app.provider('$config', configProvider as ng.IServiceProviderFactory);
app.provider('$errorHandler', errorHandlerProvider as ng.IServiceProviderFactory);
app.provider('$eventEmitter', eventEmitterProvider as ng.IServiceProviderFactory);
app.provider('$specialDayFetcher', specialDayFetcherProvider as ng.IServiceProviderFactory);
app.provider('$paginator', paginatorProvider as ng.IServiceProviderFactory);
app.provider('$storage', storageProvider as ng.IServiceProviderFactory);
app.provider('$util', utilProvider as ng.IServiceProviderFactory);

// Fetchers
app.provider('$bookingTypeFetcher', bookingTypeFetcherProvider as ng.IServiceProviderFactory);
app.provider('$carerContractTypeFetcher', carerContractTypeFetcherProvider as ng.IServiceProviderFactory);
app.provider('$clientServiceContractTypeFetcher', serviceContractTypeFetcherProvider as ng.IServiceProviderFactory);
app.provider('$userFetcher', userFetcherProvider as ng.IServiceProviderFactory);
app.provider('$settingsFetcher', settingsFetcherProvider as ng.IServiceProviderFactory);
app.provider('$teamFetcher', teamFetcherProvider as ng.IServiceProviderFactory);

app.provider('$installation', installationServiceProvider as ng.IServiceProviderFactory);

app.factory('csrfTokenInterceptor', function csrfTokenInterceptor() {
    return {
        request: function(request) {
            request.headers['x-csrf-token'] = window.CareLineLive.csrf_token;

            return request;
        },
    };
});

appConfig.$inject = [
    '$httpProvider',
    '$locationProvider',
    '$mdIconProvider',
    '$mdThemingProvider',
    '$stateProvider',
    '$urlMatcherFactoryProvider',
    '$urlRouterProvider',
];

function appConfig(
    $httpProvider,
    $locationProvider,
    $mdIconProvider,
    $mdThemingProvider,
    $stateProvider,
    $urlMatcherFactoryProvider,
    $urlRouterProvider,
) {
    console.log('test');

    $locationProvider.html5Mode(true);

    $httpProvider.interceptors.push('csrfTokenInterceptor');

    // Allow trailing slashes
    $urlMatcherFactoryProvider.strictMode(false);

    // Redirect to /install
    $urlRouterProvider.otherwise('/welcome');

    console.log('config');

    $stateProvider
        .state('welcome', {
            url: '/welcome',
            template: welcome.template,
            controller: welcome.controller,
            data: {
                title: 'Welcome',
            },
        })
        .state('users', {
            url: '/users',
            template: users.template,
            controller: users.controller,
            data: {
                title: 'Users',
            },
        })
        .state('company', {
            url: '/company',
            template: company.template,
            controller: company.controller,
            data: {
                title: 'Company',
            },
        })
        .state('teams', {
            url: '/teams',
            template: teams.template,
            controller: teams.controller,
            data: {
                title: 'Teams',
            },
        })
        .state('booking-types', {
            url: '/booking-types',
            template: bookingTypes.template,
            controller: bookingTypes.controller,
            data: {
                title: 'Booking Types',
            },
        })
        .state('carer-contracts', {
            url: '/carer-contracts',
            template: carerContracts.template,
            controller: carerContracts.controller,
            data: {
                title: 'Carer Contracts',
            },
        })
        .state('client-service-contracts', {
            url: '/client-service-contracts',
            template: clientServiceContracts.template,
            controller: clientServiceContracts.controller,
            data: {
                title: 'Client Service Contracts',
            },
        })
        .state('finish', {
            url: '/finish',
            template: finish.template,
            controller: finish.controller,
            data: {
                title: 'Finish',
            },
        });

    theme.apply($mdThemingProvider);

    $mdIconProvider.defaultIconSet('/dist/img/sprite.icons.svg', 24);
}

appRun.$inject = ['$installation', '$transitions'];

function appRun($installation, $transitions) {
    $transitions.onBefore({}, onTransitionBefore);

    onTransitionBefore.$inject = ['$transition$'];

    function onTransitionBefore(transition) {
        const to = transition.$to();

        const nextStep = $installation.nextStep;

        console.log({ nextStep });

        // Prevent advancement if no token is set
        if (to.name !== nextStep && !$installation.allowNavigationTo(to.name)) {
            return transition.router.stateService.target(nextStep);
        }
    }
}

window.app = window.app || {};

window.app.install = {
    bootrapped: false,
    bootstrap: () => {
        angular.bootstrap(document.getElementById('install'), [app.name], {
            strictDi: true,
        });
    },
};
